/* The host applications allowed to use the assistant, as a card each — superadmin only,
 * because a key registered here lets an application mint identities inside the assistant.
 *
 * An integration is not a system. The SYSTEM is the product — its key is the token's `iss`,
 * its folder holds the corpus — and it is declared beside the picker on the Documents page.
 * Registering here picks one of those and adds another way into it. That is why one product
 * can have several rows on this page, and why nothing here creates a system, which is what
 * registering used to do.
 *
 * This page is the index only; everything changeable about an integration is on its own page,
 * and the traffic figures come from `/api/adoption`, whose `by_integration` carries them all
 * at once — one line per registration, so two applications on one product are counted apart.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Input, SimpleDropdown, Textarea } from '@nextleap-al/admin-ui';

import { detailOf, write } from '../api/client';
import type { Adoption, Integration, System } from '../api/types';
import { Async, PanelCard, ResultLine } from '../components/Panel';
import { Signal, Signals } from '../components/Signal';
import { KeyBox, type IssuedKey } from '../components/KeyBox';
import { useApi } from '../hooks/useApi';
import { num, plural, shortDate } from '../lib/format';
import { useApp } from '../state/app';
import { TicketDeliveryPanel } from './TicketDelivery';

function RegisterForm({
  systems,
  onIssued,
  onDone,
}: {
  /** The systems this can be registered against. Empty means none has been declared yet, and
   *  the form says so rather than offering an empty dropdown. */
  systems: System[];
  onIssued: (key: IssuedKey) => void;
  onDone: () => void;
}) {
  const { reload } = useApp();
  // Preselected when there is only one, since a dropdown of one asks a question with a single
  // answer. Otherwise nothing, so the system is chosen rather than defaulted into.
  const [systemKey, setSystemKey] = useState(systems.length === 1 ? systems[0]!.system_key : '');
  const [displayName, setDisplayName] = useState('');
  const [origins, setOrigins] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const chosen = systems.find((s) => s.system_key === systemKey);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    const { ok, data } = await write<{ api_key?: string }>('/api/integrations', {
      system_key: systemKey,
      display_name: displayName.trim(),
      origins,
    });
    setBusy(false);
    if (!ok) {
      setResult({ ok, text: detailOf(data, 'The integration could not be registered.') });
      return;
    }
    // Named by the integration, not the system: with several on one product, "nextpass" over
    // a key box would not say which of them the key belongs to.
    if (data.api_key) {
      onIssued({ systemKey: displayName.trim() || systemKey, value: data.api_key, rotated: false });
    }
    reload();
    // Closed on success, so an empty form does not sit above the key box inviting the same
    // application to be registered twice.
    onDone();
  }

  if (!systems.length) {
    return (
      <div className="rounded-[var(--radius-md)] border border-[var(--border-light)] bg-[var(--bg-elevated)] p-4">
        <p className="max-w-prose text-sm text-[var(--text-secondary)]">
          There is no system to register against yet. An integration is one application speaking
          for a product, and the product has to exist first — add it on the{' '}
          <Link to="/documents" className="text-[var(--primary)] underline">
            Documents
          </Link>{' '}
          page, beside the system picker.
        </p>
        <Button className="mt-3" variant="ghost" size="sm" onClick={onDone}>
          Close
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="space-y-4 rounded-[var(--radius-md)] border border-[var(--border-light)] bg-[var(--bg-elevated)] p-4"
    >
      <div className="grid gap-4 md:grid-cols-2">
        <SimpleDropdown
          label="System"
          value={systemKey}
          onChange={(value) => setSystemKey(String(value))}
          placeholder="Pick the system this belongs to"
          options={systems.map((system) => ({
            value: system.system_key,
            label: system.active ? system.display_name : `${system.display_name} — turned off`,
            disabled: !system.active,
          }))}
          hint={
            chosen
              ? `Tokens issued through this integration carry iss=${chosen.system_key}, and the documents it answers from are the ones in ${chosen.corpus_folder}/.`
              : 'The product this application speaks for. Declared on the Documents page; one system can have several integrations.'
          }
        />
        <Input
          label="Integration name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          autoComplete="off"
          placeholder="e.g. NextPass web"
          hint="The application, not the product — it is how you will tell one key from another."
          required
        />
      </div>
      <Textarea
        label="Origins it may embed from"
        rows={2}
        value={origins}
        onChange={(e) => setOrigins(e.target.value)}
        placeholder={'http://localhost:8001\nhttps://nextpass.al'}
        hint="Scheme, host and port — no path. One per line. Leave empty and this integration can be verified but not embedded anywhere."
      />
      <div className="flex flex-wrap gap-2">
        <Button type="submit" isLoading={busy} disabled={!systemKey || !displayName.trim()}>
          Register and generate a key
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
      {result && <ResultLine ok={result.ok}>{result.text}</ResultLine>}
    </form>
  );
}

/** The tints a card can be given, in the order they are handed out. */
const ACCENTS = [
  'var(--chart-series-1)',
  'var(--chart-series-2)',
  'var(--chart-series-3)',
  'var(--chart-series-4)',
] as const;

/**
 * A stable colour for a system, derived from its key. It identifies only — nothing about the
 * system's health is in the hue, since the words on the card have to carry that.
 */
function accentOf(systemKey: string): string {
  const sum = [...systemKey].reduce((total, ch) => total + ch.charCodeAt(0), 0);
  return ACCENTS[sum % ACCENTS.length];
}

/**
 * One integration, as much of it as belongs on an index: three figures saying whether it is
 * being used, and at most four markers — is it on, who may use it, is it working, is it
 * misconfigured. The whole card is one link, and the markers are two words each, with the
 * longer wording on the integration's own page.
 */
function SystemCard({
  item,
  traffic,
  known,
}: {
  item: Integration;
  traffic: Adoption['by_integration'][number] | undefined;
  /** Whether the figures have arrived. Not the same as their being zero — see below. */
  known: boolean;
}) {
  // Which product this speaks for, under its own name. Always shown: with several rows on one
  // system the heading alone no longer says which corpus a card is about.
  const showKey = item.system_label !== item.display_name;

  // A system that is off gets a grey rail and a flatter card, saying at a distance what the
  // `turned off` marker says up close.
  const accent = item.active ? accentOf(item.system_key) : 'var(--chart-baseline)';

  // An em dash until the figures land, never a nought: "0 questions" is a finding, and a
  // registration genuinely absent from `by_integration` — nothing has come through it yet —
  // is the only case where 0 is the true answer.
  const figures = [
    { label: 'Questions', value: traffic?.turns },
    { label: 'Conversations', value: traffic?.conversations },
    { label: 'People', value: traffic?.users },
  ];

  return (
    <Link
      to={`/integrations/${item.id}`}
      className="group block h-full rounded-[var(--radius-lg)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--primary)]"
    >
      {/* A column, so the markers sit on the bottom edge of every card in the row rather than
          wherever the card above them happened to end. */}
      <Card
        padding="none"
        className={`relative flex h-full flex-col overflow-hidden transition-shadow hover:shadow-md ${
          item.active ? '' : 'opacity-80'
        }`}
      >
        {/* The system's colour, on the edge where a stack of cards puts them in a column and
            the eye can run down it. Decoration only: nothing is said here alone. */}
        <span
          aria-hidden="true"
          className="absolute inset-y-0 left-0 w-1.5"
          style={{ background: accent }}
        />

        <div className="flex flex-1 flex-col p-4 pl-6">
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="flex h-9 w-9 flex-none items-center justify-center rounded-[var(--radius-md)] text-base font-semibold"
              style={{
                background: `color-mix(in srgb, ${accent} 15%, transparent)`,
                color: accent,
              }}
            >
              {item.display_name.slice(0, 1).toUpperCase()}
            </span>
            <div className="min-w-0">
              <h3 className="truncate font-semibold leading-tight text-[var(--text-primary)]">
                {item.display_name}
              </h3>
              {showKey && (
                <span className="block truncate text-xs text-[var(--text-muted)]">
                  {item.system_label}
                </span>
              )}
            </div>
            {/* All that is left of "Open this system →". The whole card has always been the
                link; the sentence spelled out what the cursor, the lift on hover and an
                arrow already say, on every card in the grid at once. */}
            <span
              aria-hidden="true"
              className="ml-auto flex-none text-[var(--text-tertiary)] transition-transform group-hover:translate-x-0.5 group-hover:text-[var(--primary)]"
            >
              →
            </span>
          </div>

          <dl className="mt-4 grid grid-cols-3 gap-2 rounded-[var(--radius-md)] bg-[var(--bg-tertiary)] px-3 py-2.5">
            {figures.map((figure) => (
              <div key={figure.label} className="min-w-0">
                <dt className="text-[10px] font-medium uppercase leading-tight tracking-wide text-[var(--text-tertiary)]">
                  {figure.label}
                </dt>
                <dd className="mt-0.5 text-lg font-semibold leading-none tabular-nums text-[var(--text-primary)]">
                  {known ? num(figure.value ?? 0) : '—'}
                </dd>
              </div>
            ))}
          </dl>

          <div className="mt-auto pt-4">
            <Signals>
              {item.active ? (
                <Signal tone="good">active</Signal>
              ) : (
                <Signal tone="critical">turned off</Signal>
              )}
              {/* Read against `anyone signed in`, which is the other half of the same
                  question — "only 4 people" answers it, where "4 named people" described the
                  list and left the reader to work out that being off it means refused.
                  An enabled restriction with nobody on the list is its own marker: the panel
                  will not let you switch it on that way, but removing the last person from a
                  system already switched on gets there, and it locks out everybody in that
                  system within the half-minute. A count of zero would report that as the
                  mildest thing on the card. */}
              {item.restrict_users ? (
                item.allowed_users === 0 ? (
                  <Signal tone="critical">nobody allowed</Signal>
                ) : (
                  <Signal tone="warning">only {plural(item.allowed_users, 'person', 'people')}</Signal>
                )
              ) : (
                <Signal>anyone signed in</Signal>
              )}
              {item.last_seen_at ? (
                <Signal>used {shortDate(item.last_seen_at)}</Signal>
              ) : (
                <Signal tone="warning">never used</Signal>
              )}
              {!item.origins.length && <Signal tone="warning">no origin</Signal>}
            </Signals>
          </div>
        </div>
      </Card>
    </Link>
  );
}

export function IntegrationsPanel() {
  const { days } = useApp();
  const query = useApi<{ items: Integration[]; systems: System[] }>('/api/integrations');
  // One request behind every card, asked without a `system` so the answer holds a row for
  // each system this account may read.
  const traffic = useApi<Adoption>('/api/adoption', { days });
  const [issued, setIssued] = useState<IssuedKey | null>(null);
  const [registering, setRegistering] = useState(false);

  // Keyed by the registration's own id, not by its system: two applications on one product
  // share a `telemetry_system`, and keying on that gave both of them the product's total —
  // a registration that had never answered anything read as busy as the one that had.
  const byIntegration = new Map(
    (traffic.data?.by_integration ?? []).map((row) => [row.integration_id, row]),
  );
  // A failed request is not a quiet system: both leave the figures unknown, and an em dash
  // is what this panel prints for a number it does not have.
  const known = traffic.data != null && traffic.error == null;

  return (
    <div className="space-y-5">
      <PanelCard
        title="Integrations"
        // One line, so the cards stay on the first screen. Token signing is explained on the
        // integration's own page, next to the key.
        hint={`Which applications may use the assistant, which system each speaks for, and what each has done in the last ${plural(days, 'day')}. One system can have several — open one for its traffic in full and who may use it.`}
        action={
          !registering && (
            <Button size="sm" onClick={() => setRegistering(true)}>
              Register an integration
            </Button>
          )
        }
      >
        <div className="space-y-4">
          {registering && (
            <RegisterForm
              systems={query.data?.systems ?? []}
              onIssued={setIssued}
              onDone={() => setRegistering(false)}
            />
          )}
          {issued && <KeyBox issued={issued} onDismiss={() => setIssued(null)} />}

          <Async query={query}>
            {(data) =>
              data.items.length === 0 ? (
                <p className="max-w-prose text-sm text-[var(--text-muted)]">
                  Nothing is registered yet. Until something is, the assistant falls back to the{' '}
                  <code>ASSISTANT_HOST_SECRETS</code> and <code>ASSISTANT_HOST_ORIGINS</code>{' '}
                  values in its own <code>.env</code> — so nothing has broken, but nothing here is
                  in charge of it either.
                </p>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {data.items.map((item) => (
                    <SystemCard
                      key={item.id}
                      item={item}
                      traffic={byIntegration.get(item.id)}
                      known={known}
                    />
                  ))}
                </div>
              )
            }
          </Async>
        </div>
      </PanelCard>

      {/* The other direction. Registered systems send questions in; this is where the
          tickets those questions turn into go out. Both are one credential and one switch,
          which is why they are on one page rather than two. */}
      <TicketDeliveryPanel />
    </div>
  );
}
